import {DateTime} from 'luxon';
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled, {css} from 'styled-components';

import {Plural, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {
  RelativeDateTimeFormatsEnum,
  RelativeDateTimeFormatter,
} from '../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CrmSyncModel, CrmSyncSourcesEnum, CrmSyncStatusesEnum} from '../../../models/api/crmSyncModel';
import {renderCrmSyncSourceImage} from '../../accounts/crm/common/crmSyncHelpers';
import {ComplexTooltipContent} from '../tooltips/complexTooltipContent';

/*
 * Constants.
 */

const hoursTillNextSyncFromPrevious = 24;
const tooltipMaxWidth = 256;

/*
 * Props.
 */

interface CrmLastSyncedBannerProps {
  className?: string;
  crmSync?: CrmSyncModel;
  shouldBoldDates?: boolean;
  shouldRenderTooltip?: boolean;
  hasRunningJob: boolean;
  onManageSyncClick?: () => void;
}

/*
 * Style.
 */

const StyledLastSyncedBannerWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  gap: 10px;
  background: ${(p) => p.theme.greys.shade20};
  border-radius: 8px;
  padding: 9px 12px;
`;

const StyledImageWrapperDiv = styled.div`
  flex: 0 0 16px;
  height: 16px;
`;

const StyledTextWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  line-height: 16px;
  font-size: ${fontSizes.verySmall};
  white-space: pre;
  flex-wrap: wrap;
  min-width: 65%;
`;

interface StyledDateSpanProps {
  $shouldBold: boolean;
}

const StyledDateSpan = styled.span<StyledDateSpanProps>`
  margin-right: 4px;
  ${(p) =>
    p.$shouldBold &&
    css`
      font-weight: ${fontWeights.bold};
    `};
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: inline-block;
`;

const StyledTooltipUl = styled.ul`
  margin: 0;
  padding-left: 18px;
`;

const StyledChromelessButton = styled(ChromelessButton)`
  margin: -5px -6px -5px 0;
  padding-left: 13px;
`;

const StyledButtonContent = styled(ButtonContent)`
  font-size: ${fontSizes.tiny};
  white-space: nowrap;
  border-radius: 13px;
`;

/*
 * Component.
 */

export const CrmLastSyncedBanner: FC<CrmLastSyncedBannerProps> = (props) => {
  const {
    className,
    crmSync,
    shouldRenderTooltip,
    shouldBoldDates = false,
    hasRunningJob,
    onManageSyncClick,
  } = props;

  const previousLastSyncedAt = usePrevious(crmSync?.lastSyncedAt);
  const [shouldHideNextSync, setShouldHideNextSync] = useState(
    computeNextSyncTime(crmSync?.lastSyncedAt) < Date.now(),
  );
  const [nextSyncTime, setNextSyncTime] = useState(computeNextSyncTime(crmSync?.lastSyncedAt));

  // Listen for when we refresh, if we passed the next sync time we should hide it.
  const onRefreshCallback = useCallback(() => {
    if (crmSync?.lastSyncedAt) {
      setShouldHideNextSync(nextSyncTime < Date.now());
    }
  }, [crmSync?.lastSyncedAt, nextSyncTime]);

  // If the last sync time changed, recompute the next sync time.
  useEffect(() => {
    if (previousLastSyncedAt !== crmSync?.lastSyncedAt) {
      setNextSyncTime(computeNextSyncTime(crmSync?.lastSyncedAt));
    }
  }, [crmSync?.lastSyncedAt, previousLastSyncedAt]);

  // If we do not have a crm sync or have never synced and the sync is not currently running or sync is disabled
  if (
    !crmSync ||
    (!crmSync.lastSyncedAt && !hasRunningJob) ||
    crmSync.status === CrmSyncStatusesEnum.DISABLED
  ) {
    return null;
  }

  if (hasRunningJob) {
    return (
      <StyledLastSyncedBannerWrapperDiv className={className}>
        <StyledImageWrapperDiv>{renderCrmSyncSourceImage(crmSync.source)}</StyledImageWrapperDiv>
        <StyledTextWrapperDiv>
          <Trans id="crm-last-synced-sync-in-progress">Sync in progress...</Trans>
        </StyledTextWrapperDiv>
        {maybeRenderManageSyncButton(onManageSyncClick)}
      </StyledLastSyncedBannerWrapperDiv>
    );
  }

  return (
    <StyledLastSyncedBannerWrapperDiv className={className}>
      <StyledImageWrapperDiv>{renderCrmSyncSourceImage(crmSync.source)}</StyledImageWrapperDiv>
      <StyledTextWrapperDiv>
        <Trans id="crm-last-synced-last">Last synced</Trans>{' '}
        <StyledDateSpan $shouldBold={shouldBoldDates}>
          <RelativeDateTimeFormatter value={crmSync.lastSyncedAt} format={RelativeDateTimeFormatsEnum.FULL} />
          .
        </StyledDateSpan>{' '}
        {!shouldHideNextSync && (
          <>
            <Trans id="crm-last-synced-next">Next sync</Trans>{' '}
            <StyledDateSpan $shouldBold={shouldBoldDates}>
              <RelativeDateTimeFormatter
                value={nextSyncTime}
                format={RelativeDateTimeFormatsEnum.FULL}
                onRefresh={onRefreshCallback}
              />
              .
            </StyledDateSpan>
          </>
        )}
        {renderTooltipCoordinator(crmSync, shouldRenderTooltip)}
      </StyledTextWrapperDiv>
      {maybeRenderManageSyncButton(onManageSyncClick)}
    </StyledLastSyncedBannerWrapperDiv>
  );
};

/*
 * Helpers.
 */

function computeNextSyncTime(lastSyncedAt?: number) {
  return DateTime.fromMillis(lastSyncedAt ?? 0)
    .plus({hours: hoursTillNextSyncFromPrevious})
    .valueOf();
}

function renderTooltipCoordinator(crmSync: CrmSyncModel, shouldRenderTooltip?: boolean) {
  if (!shouldRenderTooltip) {
    return null;
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledTooltipCoordinator
          render={() => (
            <ComplexTooltipContent
              title={renderTooltipTitle(crmSync)}
              body={renderTooltipContent(crmSync)}
              maxWidth={tooltipMaxWidth}
            />
          )}
          iconColor={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}

function renderTooltipTitle(crmSync: CrmSyncModel) {
  switch (crmSync.source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return (
        <Plural
          id="crm-last-synced-salesforce-tooltip-title"
          value={Object.entries(crmSync.mapping).length}
          one="# field syncing from Salesforce"
          other="# fields syncing from Salesforce"
        />
      );
    case CrmSyncSourcesEnum.HUBSPOT:
      return (
        <Plural
          id="crm-last-synced-hubspot-tooltip-title"
          value={Object.entries(crmSync.mapping).length}
          one="# field syncing from HubSpot"
          other="# fields syncing from HubSpot"
        />
      );
    case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
      return (
        <Plural
          id="crm-last-synced-microsoft-dynamics-tooltip-title"
          value={Object.entries(crmSync.mapping).length}
          one="# field syncing from Dynamics 365"
          other="# fields syncing from Dynamics 365"
        />
      );
    case CrmSyncSourcesEnum.OUTLOOK_DIRECTORY:
      return (
        <Plural
          id="crm-last-synced-outlook-tooltip-title"
          value={Object.entries(crmSync.mapping).length}
          one="# field syncing from Outlook directory"
          other="# fields syncing from Outlook directory"
        />
      );
    default:
      return null;
  }
}

function renderTooltipContent(crmSync: CrmSyncModel) {
  switch (crmSync.source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return (
        <StyledTooltipUl>
          <li>
            <Trans id="crm-last-synced-salesforce-content-1">
              You&apos;ll be directed to Salesforce to edit those.
            </Trans>
          </li>
          <li>
            <Trans id="crm-last-synced-salesforce-content-2">
              They&apos;ll be automatically updated in Front when the next sync kicks off.
            </Trans>
          </li>
        </StyledTooltipUl>
      );
    case CrmSyncSourcesEnum.HUBSPOT:
      return (
        <StyledTooltipUl>
          <li>
            <Trans id="crm-last-synced-hubspot-content-1">
              You&apos;ll be directed to HubSpot to edit those.
            </Trans>
          </li>
          <li>
            <Trans id="crm-last-synced-hubspot-content-2">
              They&apos;ll be automatically updated in Front when the next sync kicks off.
            </Trans>
          </li>
        </StyledTooltipUl>
      );
    case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
      return (
        <StyledTooltipUl>
          <li>
            <Trans id="crm-last-synced-microsoft-dynamics-content-1">
              You&apos;ll be directed to Dynamics 365 to edit those.
            </Trans>
          </li>
          <li>
            <Trans id="crm-last-synced-microsoft-dynamics-content-2">
              They&apos;ll be automatically updated in Front when the next sync kicks off.
            </Trans>
          </li>
        </StyledTooltipUl>
      );
    case CrmSyncSourcesEnum.OUTLOOK_DIRECTORY:
      return (
        <StyledTooltipUl>
          <li>
            <Trans id="crm-last-synced-outlook-content-1">
              You&apos;ll be directed to Outlook directory to edit those.
            </Trans>
          </li>
          <li>
            <Trans id="crm-last-synced-outlook-content-2">
              They&apos;ll be automatically updated in Front when the next sync kicks off.
            </Trans>
          </li>
        </StyledTooltipUl>
      );
    default:
      return null;
  }
}

function maybeRenderManageSyncButton(onManageSyncClick?: () => void) {
  if (!onManageSyncClick) {
    return null;
  }
  return (
    <StyledChromelessButton onClick={onManageSyncClick}>
      <StyledButtonContent
        size={VisualSizesEnum.SMALL}
        variant={ButtonVariantsEnum.LINK}
        color={PaletteColorsEnum.BLUE}
      >
        <Trans id="crm-last-synced-banner-manage-sync">MANAGE SYNC</Trans>
      </StyledButtonContent>
    </StyledChromelessButton>
  );
}
